import {isNil} from 'lodash';

import {SERVER_IS_DOWN_EXCEPTION} from '/enum/errors.enum';
import {generateCallback} from '/util/component.util';

interface GenerateErrorAlertTimeoutParams {
  isShowingError: boolean;
  hideApiError: () => void;
}

/**
 * Generates a callback that closes the error popup after 10 seconds and clears timeout
 *
 * @param {GenerateErrorAlertTimeoutParams} params - Parameters
 * @returns {() => void} - Callback - A function that closes the error popup after 10 seconds and clears timeout
 */
export function generateErrorAlertTimeout({
  hideApiError,
  isShowingError,
}: GenerateErrorAlertTimeoutParams): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return () => {
    if (isShowingError) {
      timeoutId = setTimeout(() => {
        hideApiError();
      }, 10000);
    }

    if (!isNil(timeoutId)) {
      return generateCallback({func: clearTimeout, params: timeoutId});
    }
  };
}

interface GenerateApiErrorAlertNavigateAwayParams {
  currentRouteName: string;
  currentScreen: string;
  hideApiError: (params: {errorCode: string}) => void;
  isShowingServerIsDown: boolean;
  prevRouteName: string;
  setRouteName: (routeName: string) => void;
}

/**
 * Generates a callback that closes the error popup if the user navigates away
 *
 * @param {GenerateApiErrorAlertNavigateAwayParams} params - Parameters
 * @returns {() => void} - Callback - A function that closes the error popup if the user navigates away
 */
export function generateApiErrorAlertNavigateAway({
  currentRouteName,
  currentScreen,
  hideApiError,
  isShowingServerIsDown,
  prevRouteName,
  setRouteName,
}: GenerateApiErrorAlertNavigateAwayParams): () => void {
  return () => {
    setRouteName(currentScreen);

    if (isShowingServerIsDown && prevRouteName !== currentRouteName) {
      hideApiError({errorCode: SERVER_IS_DOWN_EXCEPTION});
    }
  };
}
